/**
 * `applyFollowOptimistic` / `applyUnfollowOptimistic` — optimistic-cache
 * helpers for the `useFollow` / `useUnfollow` mutations.
 *
 * The follows feature collapses four projections onto one route, so a single
 * mutation must patch several differently-keyed cache entries:
 *
 *   • check   `['users','detail', X, {kind:'check'}]`   → `{ isFollowing }`
 *   • counts  `['users','follow-counts', O, {kind:'counts'}]` → `{ followers, following }`
 *   • list    `['users','list', O, {kind:'…'}, ['user']]` → `PagedData<{user}>`
 *
 * where `X` = `vars.userId` (the user being (un)followed) and `O` = the list/
 * counts OWNER. The cached data carries no owner id, so each entry is scoped
 * by parsing the `queryKey` plus the session actor id from the SDK auth store.
 *
 * Follow/unfollow are ASYMMETRIC because the indexer publishes a follow event
 * ONLY to the followed user's `morfi.user.${X}.notifications.v0` channel — the
 * existing realtime recipes update X's followers count and protected-refetch
 * the followers list on a FOLLOW, so we skip those here to avoid double-counting.
 * There is no unfollow event, so on UNFOLLOW we patch them optimistically.
 *
 * Immutable: returns the exact `prev` reference for any entry the patch does
 * not change so structural-sharing short-circuits stay intact.
 *
 * Platform-agnostic — pure data + the platform-neutral SDK auth store +
 * TanStack `QueryClient`. No web/RN API. Safe in core.
 */

import type { QueryClient } from '@tanstack/react-query';

import { getSdkAuthStore } from '../auth/state/auth-state.js';
import { markOptimisticFollow } from './follow-echo-dedup.js';

// ── Structural cache shapes (minimal) ──────────────────────────────────────

interface FollowRowUser {
  readonly userId?: string | null;
  readonly walletAddress?: string;
  readonly displayName?: string | null;
  readonly handle?: string | null;
  readonly avatarUrl?: string | null;
  readonly avatarNsfw?: boolean;
  readonly avatarPreferences?: { readonly rasterise: boolean };
  readonly connectedProviders?: readonly unknown[];
}
interface FollowRow {
  readonly user?: FollowRowUser;
}
interface PagedFollowData {
  readonly data?: ReadonlyArray<FollowRow>;
}
interface CountsData {
  readonly followers?: number;
  readonly following?: number;
}
interface CheckData {
  readonly isFollowing?: boolean;
}

// ── queryKey parsers ───────────────────────────────────────────────────────

function keySection(queryKey: readonly unknown[]): 'detail' | 'list' | null {
  const s = queryKey[1];
  if (s === 'follow-counts') return 'detail';
  return s === 'detail' || s === 'list' ? s : null;
}
function keyOwner(queryKey: readonly unknown[]): string | null {
  return typeof queryKey[2] === 'string' ? queryKey[2] : null;
}
function keyKind(queryKey: readonly unknown[]): string | null {
  const disc = queryKey[3];
  if (disc !== null && typeof disc === 'object' && 'kind' in disc) {
    const k = (disc as { kind?: unknown }).kind;
    return typeof k === 'string' ? k : null;
  }
  return null;
}

function currentActorId(): string | null {
  return getSdkAuthStore().current().user?.id ?? null;
}

// ── Row builder for the FOLLOW prepend ─────────────────────────────────────

function isProfileShaped(data: unknown): data is Record<string, unknown> {
  return (
    data !== null &&
    typeof data === 'object' &&
    ('displayName' in data || 'handle' in data || 'avatarUrl' in data)
  );
}

export function buildFollowRow(queryClient: QueryClient, userId: string): FollowRow {
  // Prefer the canonical public identity populated by profiles and cards.
  // Older detail snapshots can still supply identity during cache migration.
  const entries = [
    ...queryClient.getQueriesData({ queryKey: ['users', 'entity', userId], exact: true }),
    ...queryClient.getQueriesData({ queryKey: ['users', 'detail', userId] }),
  ];
  for (const [, data] of entries) {
    if (!isProfileShaped(data)) continue;
    const rasterise =
      typeof data.avatarPreferences === 'object' &&
      data.avatarPreferences !== null &&
      (data.avatarPreferences as { rasterise?: unknown }).rasterise === true;
    return {
      user: {
        userId,
        walletAddress: typeof data.pubkey === 'string' ? data.pubkey : '',
        displayName: typeof data.displayName === 'string' ? data.displayName : null,
        handle: typeof data.handle === 'string' ? data.handle : null,
        avatarUrl: typeof data.avatarUrl === 'string' ? data.avatarUrl : null,
        avatarNsfw: data.avatarNsfw === true,
        avatarPreferences: { rasterise },
        connectedProviders: Array.isArray(data.connectedProviders) ? data.connectedProviders : [],
      },
    };
  }
  return {
    user: {
      userId,
      walletAddress: '',
      displayName: null,
      handle: null,
      avatarUrl: null,
      avatarNsfw: false,
      avatarPreferences: { rasterise: false },
      connectedProviders: [],
    },
  };
}

// ── Field patches (immutable) ──────────────────────────────────────────────

function withCheck<T>(prev: T, value: boolean): T {
  const cur = prev as CheckData;
  if (cur.isFollowing === value) return prev;
  return { ...(prev as object), isFollowing: value } as T;
}

function withCountDelta<T>(prev: T, field: 'followers' | 'following', delta: number): T {
  const cur = prev as CountsData;
  const next = Math.max(0, (cur[field] ?? 0) + delta);
  if (next === cur[field]) return prev;
  return { ...(prev as object), [field]: next } as T;
}

function withRowRemoved<T>(prev: T, rows: ReadonlyArray<FollowRow>, userId: string): T {
  const filtered = rows.filter((r) => r.user?.userId !== userId);
  if (filtered.length === rows.length) return prev;
  return { ...(prev as object), data: filtered } as T;
}

function withRowPrepended<T>(prev: T, rows: ReadonlyArray<FollowRow>, row: FollowRow): T {
  const id = row.user?.userId;
  if (id !== undefined && rows.some((r) => r.user?.userId === id)) return prev;
  return { ...(prev as object), data: [row, ...rows] } as T;
}

// ── Core ───────────────────────────────────────────────────────────────────

function applyFollowMutation<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: readonly unknown[],
  queryClient: QueryClient,
  isFollow: boolean,
): T {
  if (prev === null || prev === undefined || typeof prev !== 'object') return prev;
  const targetId = typeof vars.userId === 'string' && vars.userId.length > 0 ? vars.userId : null;
  if (targetId === null) return prev;

  const section = keySection(queryKey);
  const owner = keyOwner(queryKey);
  const kind = keyKind(queryKey);
  const actor = currentActorId();

  if (section === 'detail') {
    // viewer→X follow state.
    if (kind === 'check' && owner === targetId) return withCheck(prev, isFollow);

    if (kind === 'counts') {
      const delta = isFollow ? 1 : -1;
      // Actor's own "following" count — no realtime event reaches the actor.
      if (actor !== null && owner === actor) return withCountDelta(prev, 'following', delta);
      // X's "followers" count — patch optimistically for INSTANT feedback (the
      // viewer following X sees X's count tick up immediately). The realtime
      // FOLLOW_RECEIVED echo on X's channel reaches the actor too (their
      // `useGetFollowsCounts(X)` is subscribed to it), so we record the follow
      // here and `incrementFollowersCount` swallows that one echo — no
      // double-count, while bystanders / X's own view still get the realtime +1.
      if (owner === targetId) {
        if (isFollow) markOptimisticFollow(targetId);
        return withCountDelta(prev, 'followers', delta);
      }
    }
    return prev;
  }

  if (section === 'list') {
    const env = prev as PagedFollowData;
    if (!Array.isArray(env.data)) return prev;
    const rows = env.data;

    // Actor's FOLLOWING list — add/remove X (no realtime reaches the actor).
    if (actor !== null && owner === actor && kind === 'following') {
      return isFollow
        ? withRowPrepended(prev, rows, buildFollowRow(queryClient, targetId))
        : withRowRemoved(prev, rows, targetId);
    }

    // X's FOLLOWERS list — the actor appears/disappears. Realtime prepends the
    // actor on FOLLOW; only patch the removal on UNFOLLOW.
    if (actor !== null && owner === targetId && kind === 'followers' && !isFollow) {
      return withRowRemoved(prev, rows, actor);
    }
    return prev;
  }

  return prev;
}

export function applyFollowOptimistic<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: readonly unknown[],
  queryClient: QueryClient,
): T {
  return applyFollowMutation(prev, vars, queryKey, queryClient, true);
}

export function applyUnfollowOptimistic<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: readonly unknown[],
  queryClient: QueryClient,
): T {
  return applyFollowMutation(prev, vars, queryKey, queryClient, false);
}

/** Public follower/following aggregates remain independent of the viewer's
 * relationship entity. This narrowly patches the count projection only. */
export function applyFollowCountsOptimistic<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: readonly unknown[],
  queryClient: QueryClient,
): T {
  if (keyKind(queryKey) !== 'counts') return prev;
  return applyFollowMutation(prev, vars, queryKey, queryClient, true);
}

export function applyUnfollowCountsOptimistic<T>(
  prev: T,
  vars: Record<string, unknown>,
  queryKey: readonly unknown[],
  queryClient: QueryClient,
): T {
  if (keyKind(queryKey) !== 'counts') return prev;
  return applyFollowMutation(prev, vars, queryKey, queryClient, false);
}
